<template>
  <!-- BOE-51 — the install offer, out from behind the account menu. Shown on
       the signed-in app (every tenant app) and over Boer's public landing page,
       only while there is something to offer (pwa/install.ts#installOfferFor):
       a real "Install app" button when Chromium holds a prompt, the
       Share → Add to Home Screen instruction on iOS, nothing once installed or
       dismissed. Bottom-anchored like UpdateBanner, and it steps aside while an
       update is offered so the two never stack.

       Structural rules are `!important` for the same reason as UpdateBanner:
       this renders in the SHELL but sits over a MODULE, whose unlayered
       Vuetify reset otherwise wins. -->
  <Transition name="inst">
    <div v-if="visible" class="inst" role="region" :aria-label="title">
      <span class="inst__icon" aria-hidden="true">
        <span class="material-icons">{{ offer === "ios-hint" ? "ios_share" : "add_to_home_screen" }}</span>
      </span>
      <div class="inst__text">
        <p class="inst__title">{{ title }}</p>
        <p class="inst__body">{{ offer === "ios-hint" ? t("install.iosHint") : t("install.prompt") }}</p>
      </div>
      <div class="inst__actions">
        <button type="button" class="inst__btn inst__later" @click="dismissInstall()">
          {{ offer === "ios-hint" ? t("install.gotIt") : t("install.notNow") }}
        </button>
        <button v-if="offer === 'prompt'" type="button" class="inst__btn inst__apply" @click="install">
          {{ t("account.installApp") }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { dismissInstall, installOffer, promptInstall } from "@/pwa/install";
import { appUpdate } from "@/pwa/appUpdate";
import { useSessionStore } from "@/stores/session";
import { isBoerMarketingLanding } from "@/landing/isBoerLanding";

const { t } = useI18n();
const session = useSessionStore();
const offer = installOffer;

/** Boer's public landing — the one pre-sign-in screen that carries the offer. */
const onBoerLanding = computed(() => {
  if (session.status !== "signed-out" && session.status !== "error") return false;
  const loginPage = session.tenant?.loginPage;
  if (loginPage?.mode !== "custom") return false;
  return isBoerMarketingLanding({
    slug: session.tenant?.slug,
    htmlUrl: loginPage.htmlUrl,
    hostname: session.host || window.location.hostname,
  });
});

const visible = computed(
  () => offer.value !== null && !appUpdate.ready.value && (session.status === "ready" || onBoerLanding.value),
);

const title = computed(() => {
  const app = session.tenant?.name?.trim();
  return app ? t("install.title", { app }) : t("install.titleAnon");
});

async function install(): Promise<void> {
  await promptInstall();
}
</script>

<style scoped>
.inst {
  position: fixed !important;
  left: 12px !important;
  right: 12px !important;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
  margin: 0 auto !important;
  z-index: 2390 !important;
  box-sizing: border-box !important;

  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  gap: 12px !important;
  max-width: 560px !important;
  padding: 14px 16px !important;

  border-radius: 12px !important;
  background: #ffffff !important;
  color: #1a1a1a !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.24) !important;
}

.inst__icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: none !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 10px !important;
  background: rgba(0, 0, 0, 0.06) !important;
  color: inherit !important;
}

.inst__text {
  flex: 1 1 200px !important;
  min-width: 0 !important;
}

.inst__title,
.inst__body {
  margin: 0 !important;
  padding: 0 !important;
  color: inherit !important;
}

.inst__title {
  font-size: 15px !important;
  line-height: 1.35 !important;
  font-weight: 600 !important;
}

.inst__body {
  font-size: 14px !important;
  line-height: 1.4 !important;
  opacity: 0.8 !important;
}

.inst__actions {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 8px !important;
  flex: 1 1 100% !important;
}

.inst__btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 1 1 auto !important;
  min-height: 44px !important;
  padding: 0 18px !important;
  border: 0 !important;
  border-radius: 8px !important;
  font-family: inherit !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  cursor: pointer !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

.inst__later {
  background: rgba(0, 0, 0, 0.06) !important;
  color: #1a1a1a !important;
}

.inst__apply {
  background: #0e4d3c !important;
  background: var(--tenant-primary, #0e4d3c) !important;
  color: #ffffff !important;
}

.inst__btn:focus-visible {
  outline: 2px solid #0e4d3c !important;
  outline-offset: 2px !important;
}

@media (min-width: 480px) {
  .inst__actions {
    flex: none !important;
  }
  .inst__btn {
    flex: none !important;
  }
}

body[data-theme="dark"] .inst {
  background: #1e1e1e !important;
  color: #f2f2f2 !important;
  border-color: rgba(255, 255, 255, 0.16) !important;
}

body[data-theme="dark"] .inst__icon,
body[data-theme="dark"] .inst__later {
  background: rgba(255, 255, 255, 0.12) !important;
  color: #f2f2f2 !important;
}

.inst-enter-active,
.inst-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease !important;
}

.inst-enter-from,
.inst-leave-to {
  opacity: 0 !important;
  transform: translateY(12px) !important;
}

@media (prefers-reduced-motion: reduce) {
  .inst-enter-active,
  .inst-leave-active {
    transition: none !important;
  }
}
</style>
